import {Icon, Show, Space, ThemeIconKey, VStack} from 'platform/foundation';

import {Ref, useId} from 'react';

import {isNil, isNotNil} from 'ramda';

import {isNotNilOrEmpty, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../../types/FormControlProps';
import {HelperText} from '../../HelperText/HelperText';
import {Label} from '../../Label/Label';
import {PlatformInputElement} from '../../TextInput/components/PlatformInputElement';
import {PlatformInputWrapper} from '../../TextInput/components/PlatformInputWrapper';
import {HTMLAutocomplete} from '../../TextInput/HTMLAutocomplete';

export interface DatePickerInputProps extends FormControlProps<string | null>, TestIdProps {
  autoComplete?: HTMLAutocomplete;
  placeholder?: string | Nullish;
  onClick?: VoidFunction;
  leftIcon?: ThemeIconKey;
  isRecommended?: boolean;
  onClearInput?: () => void;
  ref?: Ref<HTMLInputElement>;
  /**
   * @about Accessible name for the input when no visible `label` is rendered. Ignored
   * whenever `label` is set, so the visible label always wins.
   */
  'aria-label'?: string;
}

export function DatePickerInput(props: DatePickerInputProps) {
  const generatedId = useId();
  const id = props.id ?? generatedId;

  const isInvalid = props.isInvalid || !!props.errorMessage;
  const isClearable =
    isNotNil(props.onClearInput) && isNotNilOrEmpty(props.value) && !props.isDisabled;

  return (
    <VStack>
      <Label
        id={id}
        isRequired={props.isRequired}
        tooltip={props.tooltip}
        isRecommended={!!props.isRecommended}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>
      <PlatformInputWrapper
        onClick={props.onClick}
        isDisabled={props.isDisabled}
        isInvalid={isInvalid}
        isRecommended={!!props.isRecommended}
      >
        <Show when={props.leftIcon}>
          <Icon value={props.leftIcon} size={4} />
          <Space horizontal={2} />
        </Show>

        <PlatformInputElement
          className="!text-[length:var(--text-size-small)]"
          id={id}
          autoComplete={props.autoComplete}
          type="text"
          name={props.name}
          isInvalid={isInvalid}
          isRequired={props.isRequired}
          isDisabled={!!props.isDisabled}
          placeholder={props.placeholder}
          onChange={props.onChange}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onKeyDown={props.onKeyDown}
          onPaste={props.onPaste}
          value={props.value}
          ref={props.ref}
          data-name={props.name}
          aria-label={isNil(props.label) ? props['aria-label'] : undefined}
          data-testid={props['data-testid']}
        />

        {/* To always leave space for the clear button */}
        <Show when={!isClearable && isNotNilOrEmpty(props.onClearInput)}>
          <Space horizontal={6} />
        </Show>

        <Show when={isClearable}>
          <Space horizontal={2} />
          <Icon
            value="navigation/close"
            size={4}
            color="text.secondary"
            onClick={(event) => {
              event.stopPropagation();
              props.onClearInput?.();
            }}
            data-testid={suffixTestId('clearButton', props)}
          />
        </Show>
      </PlatformInputWrapper>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
